import { useState } from "react";
import { Play } from "lucide-react";

interface YouTubeFacadeProps {
  videoId: string;
  title: string;
  className?: string;
}

/**
 * Click-to-load YouTube facade.
 * Avoids loading ~1MB of YouTube JS + third-party cookies until the user
 * actually wants to watch the video. Big win for Best Practices and TBT.
 */
export function YouTubeFacade({ videoId, title, className = "" }: YouTubeFacadeProps) {
  const [active, setActive] = useState(false);

  if (active) {
    return (
      <iframe
        className={className}
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setActive(true)}
      aria-label={`Play video: ${title}`}
      className={`${className} group relative block w-full h-full cursor-pointer border-0 p-0`}
      style={{
        backgroundImage: `url(https://i.ytimg.com/vi/${videoId}/hqdefault.jpg)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <span className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex items-center justify-center w-20 h-20 rounded-full bg-red-600 group-hover:bg-red-700 shadow-2xl transition-colors">
          <Play className="w-9 h-9 text-white fill-white ml-1" />
        </span>
      </span>
    </button>
  );
}
